import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';

const SnackbarContext = createContext(null);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within SnackbarProvider');
    }
    return context;
};

export const SnackbarProvider = ({ children }) => {
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success', // success, error, warning, info
        title: null,
        duration: 4000,
    });

    const showSnackbar = useCallback((message, severity = 'success', title = null, duration = 4000) => {
        setSnackbar({
            open: true,
            message,
            severity,
            title,
            duration,
        });
    }, []);

    const showSuccess = useCallback((message, title = null) => {
        showSnackbar(message, 'success', title);
    }, [showSnackbar]);

    const showError = useCallback((message, title = null) => {
        showSnackbar(message, 'error', title, 6000);
    }, [showSnackbar]);

    const showWarning = useCallback((message, title = null) => {
        showSnackbar(message, 'warning', title);
    }, [showSnackbar]);

    const showInfo = useCallback((message, title = null) => {
        showSnackbar(message, 'info', title);
    }, [showSnackbar]);

    const showOfflineSaved = useCallback((entityName = 'Data') => {
        showSnackbar(`${entityName} saved offline — will sync when online`, 'info', null, 5000);
    }, [showSnackbar]);

    const showSyncSuccess = useCallback((entityName = 'Data') => {
        showSnackbar(`${entityName} synced successfully`, 'success', null, 3000);
    }, [showSnackbar]);

    const showSyncFailed = useCallback((entityName = 'Data') => {
        showSnackbar(`Sync failed for ${entityName} — retrying`, 'error', null, 6000);
    }, [showSnackbar]);

    const handleClose = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const value = useMemo(() => ({
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showOfflineSaved,
        showSyncSuccess,
        showSyncFailed,
    }), [showSnackbar, showSuccess, showError, showWarning, showInfo, showOfflineSaved, showSyncSuccess, showSyncFailed]);

    return (
        <SnackbarContext.Provider value={value}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.title && <AlertTitle>{snackbar.title}</AlertTitle>}
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};


